import { closeErrorDetails, useErrorDetails } from "@/lib/errors/error-details-store";
import { Context } from "@/util-components/root/context";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";
import { useAuth0 } from "@auth0/auth0-react";
import { AlertTriangle, Bug, LogOut, Copy } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useErrorReport } from "@/lib/hooks/use-error-report";
import { toast } from "@/lib/toast";
import { Button } from "./button";
import { Dialog, DialogContent, DialogFooter } from "./dialog";

const ErrorModal = () => {
  const { error, setError } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const { sendReport } = useErrorReport();
  const activeError = useErrorDetails();
  const [displayError, setDisplayError] = useState(activeError);

  useEffect(() => {
    if (activeError) {
      setDisplayError(activeError);
    }
  }, [activeError]);

  const onClose = (withoutReload?: boolean) => {
    closeErrorDetails();
    if (withoutReload === undefined || withoutReload) {
      return;
    }

    window.location.reload();
  };

  useEffect(() => {
    if (!error?.location?.redirect) {
      return;
    }

    const currentPath = location.pathname.replace(/\/$/, "");
    const targetPath = error.location.path.replace(/\/$/, "");
    const isAlreadyThere =
      targetPath === ""
        ? currentPath === ""
        : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);

    if (isAlreadyThere) {
      setError?.(undefined);
      return;
    }

    navigate(error.location.path + (error.mail ? `?mail=${error.mail}` : ""));
    setError?.(undefined);
  }, [error, navigate, setError, location.pathname]);

  const logOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleSendReport = () => {
    if (!displayError) return;
    sendReport(displayError);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("In die Zwischenablage kopiert");
  };

  return (
    <Dialog
      open={Boolean(activeError)}
      onOpenChange={(value) => {
        if (value === false) {
          onClose(!displayError?.withReload);
        }
      }}
    >
      <DialogContent
        className="sm:max-w-2xl p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        isClosable={displayError?.isClosable ?? true}
      >
        {displayError && (
          <>
            <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
              <StandardDialogHeader
                icon={AlertTriangle}
                title={displayError.title || "Ein Fehler ist aufgetreten"}
                description="Entschuldigung, das hätte nicht passieren sollen."
                variant="red"
                className="p-0 border-none"
              />
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 space-y-4">
                <div className="rounded-xl bg-red-50/50 border border-red-100/50 p-4 text-sm text-gray-600 leading-relaxed">
                  {displayError.message}
                </div>
                {(displayError.traceId || displayError.correlationId) && (
                  <div className="space-y-2">
                    {displayError.traceId && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600 flex justify-between items-center group">
                        <div className="flex-1 overflow-hidden text-ellipsis mr-2">
                          <span className="font-medium mr-1">Trace-ID:</span>
                          {displayError.traceId}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 transition-opacity flex-shrink-0"
                          onClick={() => handleCopy(displayError.traceId!)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {displayError.correlationId && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600 flex justify-between items-center group">
                        <div className="flex-1 overflow-hidden text-ellipsis mr-2">
                          <span className="font-medium mr-1">Correlation-ID:</span>
                          {displayError.correlationId}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 transition-opacity flex-shrink-0"
                          onClick={() => handleCopy(displayError.correlationId!)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {displayError.debugContext && (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                      <div>Zeitpunkt: {displayError.debugContext.timestamp}</div>
                      <div>
                        Request: {displayError.debugContext.request.method} {displayError.debugContext.request.url}
                      </div>
                      <div>
                        Response: {displayError.debugContext.response.status} {displayError.debugContext.response.statusText}
                      </div>
                    </div>
                    {displayError.debugContext.stackTrace && (
                      <div className="rounded-xl border border-gray-200 bg-gray-950 px-4 py-3 text-xs text-gray-100">
                        <div className="mb-2 font-medium text-gray-300">Stack Trace</div>
                        <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">
                          {displayError.debugContext.stackTrace}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSendReport}
                className="text-muted-foreground hover:text-foreground hover:bg-gray-200/50 gap-2 text-xs h-11 px-4 w-full sm:w-auto justify-start sm:justify-center rounded-xl font-semibold"
              >
                <Bug className="h-3.5 w-3.5" />
                Fehlerbericht senden
              </Button>

              <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                {(displayError.isClosable ?? true) && (
                  <Button
                    className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold transition-all sm:w-auto sm:justify-start"
                    variant="outline"
                    onClick={() => onClose(!displayError.withReload)}
                  >
                    {displayError.withReload ? "Neu laden" : "Schließen"}
                  </Button>
                )}

                {displayError.location?.path && (
                  <>
                    {displayError.location.path === "/login" ? (
                      <Button
                        className="h-11 w-full justify-center rounded-xl px-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-sm transition-all sm:w-auto sm:justify-start"
                        onClick={() => logOut()}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Ausloggen
                      </Button>
                    ) : (
                      <Link to={displayError.location.path} className="flex-1 sm:flex-none w-full sm:w-auto">
                        <Button
                          className="h-11 w-full justify-center rounded-xl px-6 sm:px-8 font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all"
                          onClick={() => onClose(true)}
                        >
                          {displayError.entity || "Weiter"}
                        </Button>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ErrorModal;
